<template>
  <div>
    <!-- Search Modal -->
    <div v-if="showIngredientSearchModal" class="modal is-active">
      <div class="modal-background" @click="closeIngredientSearchModal"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <h4 class="modal-card-title">
            Search for Replacement Ingredient
            <span v-if="foodMode === NONFOOD">- Non-Food</span>
          </h4>
          <button class="delete" aria-label="close" @click="closeIngredientSearchModal"></button>
        </header>
        <div class="modal-card-subhead">
          <p v-if="foodMode === NONFOOD" class="mb-2">
            <em>{{ ingredientName }}</em> is a non-food ingredient.
          </p>
          <p>
            Select
            {{ foodMode === FOOD ? "an ingredient" : "another non-food ingredient" }}
            below to replace <em>{{ ingredientName }}</em> in
            <strong>{{ totalRecipes }}</strong> recipe{{ totalRecipes > 1 ? "s" : "" }}.
          </p>
        </div>

        <section class="modal-card-body">
          <div class="search-container">
            <!-- Search Box -->
            <div class="field has-addons mb-0">
              <div class="control has-icons-left is-expanded is-rounded">
                <span class="icon is-left">
                  <i class="fas fa-search"></i>
                </span>
                <input
                  v-model="queryInput"
                  class="search-ingredients input is-rounded"
                  type="search"
                  placeholder="Search ingredients"
                />
              </div>
              <div class="control">
                <button
                  class="button is-rounded"
                  :class="{ 'is-active is-info': showIngredientFilters }"
                  @click="showIngredientFilters = !showIngredientFilters"
                >
                  <span class="icon is-left">
                    <i class="fa-light fa-sliders-h"></i>
                  </span>
                </button>
              </div>
            </div>

            <!-- Advanced Filters -->
            <div
              v-if="showIngredientFilters"
              class="well-beneath-search-field responsive-no-well scope-filter-options"
            >
              <div class="columns">
                <div class="column">
                  <label class="label mb-1">
                    Filter
                    <strong v-if="foodMode === NONFOOD">non-food</strong>
                    ingredients
                  </label>
                  <div class="control">
                    <label
                      v-for="option in Object.entries(scopeFilterOptions)"
                      :key="option[0]"
                      class="radio"
                    >
                      <input v-model="scopeFilter" type="radio" :value="option[0]" />
                      {{ option[1] }}
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="list-container">
            <div v-if="ingredientsLoaded">
              <IngredientListStatic
                :replacement-mode="true"
                :show-ingredient-key="true"
                :enable-click-to-sort="false"
                :show-updated-at="false"
                :ingredients="ingredientSearchStore.visibleIngredients"
                :is-loading="ingredientSearchStore.loading"
                :user-id="userId"
                :show-emails="adminMode"
                :no-results-text="noResultsText"
                clear-search-text="Reset search"
                ingredient-name-class="ingredient-name-within-modal"
                table-class="is-narrow"
                table-head-class="is-hidden-mobile"
                @select-replacement="openReplacementConfirmModal"
                @clear-search="resetSearch"
              />

              <PagerWidget
                v-if="totalIngredients > perPage"
                :model-value="ingredientsCurrentPage"
                :total-entries="totalIngredients"
                :per-page="perPage"
                @update:model-value="changePage"
              />
            </div>
          </div>
        </section>

        <footer class="modal-card-foot">
          <button class="button" @click="closeIngredientSearchModal">Cancel</button>
        </footer>
      </div>
    </div>

    <!-- Confirmation Modal -->
    <div v-if="showReplaceIngredientConfirmModal" class="modal is-active">
      <div class="modal-background" @click="closeReplacementConfirmModal"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">
            Warning! About to modify <strong>{{ totalRecipes }}</strong> recipe{{
              totalRecipes > 1 ? "s" : ""
            }}.
          </p>
          <button class="delete" aria-label="close" @click="closeReplacementConfirmModal"></button>
        </header>
        <section class="modal-card-body">
          <p>
            Please confirm that you want to replace the current instances of this ingredient
            <strong>({{ ingredientName?.trim() }})</strong> with the selected ingredient
            <strong>({{ selectedReplacementIngredient?.name?.trim() }}).</strong>
          </p>
          <p>
            The <strong>units and quantity may change</strong> if the same units aren't available in
            the replacement ingredient, but the overall amount will stay the same. This can't be
            undone without re-replacing the ingredient.
          </p>
          <p>
            This will <strong>not perform</strong> any replacements that result in a recipe that
            <strong>directly or indirectly references itself</strong> via custom ingredients created
            from subrecipes.
          </p>
          <p>
            Make sure to <strong>preview the ingredient</strong> and of course
            <strong>double-check the affected recipes</strong> after, including ingredient
            statements and costs (which won&apos;t be adjusted).
          </p>
        </section>
        <footer class="modal-card-foot is-flex-mobile is-flex-direction-column-mobile">
          <button class="button" @click="closeReplacementConfirmModal">
            Nevermind, I&apos;m not sure yet.
          </button>
          <span class="is-block-mobile is-invisible-desktop p-2" />
          <button class="button is-danger" @click="handleReplaceIngredient">
            Replace&nbsp;
            <span v-if="totalRecipes > 1">ingredients in these {{ totalRecipes }} recipes</span>
            <span v-if="totalRecipes === 1">ingredient in this recipe</span>
          </button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script setup>
  import IngredientListStatic from "../ingredient-list-static.vue";
  import PagerWidget from "../pager-widget.vue";

  import { RECIPES_CONTAINING_INGREDIENT_SEARCH_STORE, FOOD, NONFOOD } from "../stores/constants";
  import { useIngredientSearchStore } from "../stores/ingredient-search-store";
  import { useRecipeSearchStore } from "../stores/recipe-search-store";

  import { errorHelpTip } from "../lib/constants";

  import { replaceIngredientPath, stashedFilePath } from "~/routes";
  import { Flash } from "~/jose_modern/flash";

  import { storeToRefs } from "pinia";
  import { onMounted, ref, watch, computed } from "vue";
  import { onKeyStroke } from "@vueuse/core";

  const DEFAULT_SCOPE_FILTER = "recipe_ingredients_only";
  const props = defineProps({
    ingredientId: { type: Number, required: true },
    ingredientName: { type: String, required: true },
    userId: { type: Number, required: true },
    adminMode: { type: Boolean, default: false },
    foodMode: { type: String, default: FOOD },
    scopeFilterOptions: { type: Object, required: true },
    scopesAllowingBlankQuery: { type: Array, default: () => [] },
  });
  defineExpose({
    openIngredientSearchModal,
  });

  const showIngredientSearchModal = ref(false);
  const showIngredientFilters = ref(true);
  const showReplaceIngredientConfirmModal = ref(false);
  const selectedReplacementIngredient = ref(null);
  const queryInput = ref("");

  // We use the same recipeSearchStore instance as show-recipes-for-ingredient (and ingredient-replacement-search);
  // no need to initialize in onMounted, as show-recipes-for-ingredient should handle initializing it.
  const recipeSearchStore = useRecipeSearchStore(RECIPES_CONTAINING_INGREDIENT_SEARCH_STORE);
  const { totalEntries: totalRecipes } = storeToRefs(recipeSearchStore);

  const ingredientSearchStore = useIngredientSearchStore.ingredientReplacementSearch({
    foodMode: props.foodMode,
    showEmails: props.adminMode,
  });

  const {
    loaded: ingredientsLoaded,
    currentPage: ingredientsCurrentPage,
    totalEntries: totalIngredients,
    perPage,
  } = storeToRefs(ingredientSearchStore);

  onMounted(() => {
    // Restore query from store (i.e. from sessionStorage)
    queryInput.value = ingredientSearchStore.q;

    // Set default scope filter if it didn't load from sessionStorage etc.
    if (!ingredientSearchStore.scopeFilter) {
      ingredientSearchStore.setScopeFilter(DEFAULT_SCOPE_FILTER);
    }

    ingredientSearchStore.setUserId(props.userId);
    ingredientSearchStore.setExcludeIngredientId(props.ingredientId);

    // Close modals from Escape key.
    onKeyStroke("Escape", () => {
      closeIngredientSearchModal();
      closeReplacementConfirmModal();
    });

    // Initialize ingredient search
    if (window?.requestIdleCallback) {
      window.requestIdleCallback(updateSearch);
    } else if (window?.requestAnimationFrame) {
      window.requestAnimationFrame(updateSearch);
    } else {
      updateSearch();
    }
  });

  /// Reactively search (minding that some scopes allow blank queries, and some don't).
  const trimmedQuery = computed(() => queryInput.value.trim());
  const scopeFilter = computed({
    get() {
      return ingredientSearchStore.scopeFilter;
    },
    set(value) {
      ingredientSearchStore.setScopeFilter(value);
    },
  });

  function updateSearch() {
    const q = trimmedQuery.value;
    const allowsBlankQuery = props.scopesAllowingBlankQuery.includes(scopeFilter.value);

    if (q || allowsBlankQuery) {
      // setQueryFilter searches automatically when query changes.
      ingredientSearchStore.setQueryFilter(q);
      if (!ingredientSearchStore.loading) {
        // If query didn't change - but we still hit this path because scopeFilter changed -
        // then may still need to trigger search.
        ingredientSearchStore.loadIngredientPage(1);
      }
    } else {
      // If query is blank and the scope doesn't allow blank queries, reset to no results.
      // (This is the same behavior as if we hit the server, just skipping the flash of loading and null state.)
      ingredientSearchStore.resetResultsState();
    }
  }
  watch([trimmedQuery, scopeFilter], updateSearch);

  const noResultsText = computed(() => {
    const scopeAllowsBlankQuery = props.scopesAllowingBlankQuery.includes(scopeFilter.value);
    if (!trimmedQuery.value && !scopeAllowsBlankQuery) {
      return "Start typing an ingredient name to search.";
    } else {
      return null;
    }
  });

  /// Other state changes - opening/closing modals, confirmation, etc.
  async function openIngredientSearchModal() {
    const ok = await ingredientSearchStore.loadInitial();
    if (!ok) {
      // Reset search in case the params loaded from sessionStorage were the cause of the error
      resetSearch();
    }
    showIngredientSearchModal.value = true;
  }

  function closeIngredientSearchModal() {
    showIngredientSearchModal.value = false;
  }

  function resetSearch() {
    ingredientSearchStore.resetSearch({ scopeFilter: DEFAULT_SCOPE_FILTER });
    queryInput.value = "";
    showIngredientFilters.value = true;
  }

  function openReplacementConfirmModal(ingredient) {
    selectedReplacementIngredient.value = ingredient;
    showReplaceIngredientConfirmModal.value = true;
  }

  function closeReplacementConfirmModal() {
    showReplaceIngredientConfirmModal.value = false;
  }

  function changePage(newPage) {
    ingredientSearchStore.loadIngredientPage(newPage);
  }

  /// The rest deals with replacing the ingredient and flashing success/error toast notifications.
  const flashNotice = (msg) => Flash.notice(msg);
  const flashSuccess = (msg) => Flash.success(msg, { duration: 30 * 1000, closeOnClick: false });
  const flashError = (msg) => Flash.error(msg, { duration: 30 * 1000 });
  const flashClear = () => Flash.hideExisting();

  async function handleReplaceIngredient(pollingInterval = 3000, timeout = 60 * 1000) {
    // Ingredient replacement happens only for the filtered recipes. To make this happen, we get the
    // full non-paginated list of recipe IDs and include that as the JSON body.
    const allRecipeIds = await recipeSearchStore.fetchSelection();
    const body = JSON.stringify({ recipe_ids: allRecipeIds });

    flashClear();

    const url = replaceIngredientPath(selectedReplacementIngredient.value.id, {
      existing_ingredient: props.ingredientId,
      user_id: props.userId,
    });

    try {
      const response = await fetch(url, {
        body,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
      });

      if (response.ok) {
        let didFlashInitiated = false;

        /// Close modal and start notification flow.
        closeReplacementConfirmModal();
        closeIngredientSearchModal();

        // Resetting the search query is a little helpful in the case of "wait, I want to replace the replacement ingredient".
        // It's an edge case but it might happen and we can make it less confusing if it does happen.
        ingredientSearchStore.setQueryFilter("");

        const maybeFlashInitiated = () => {
          if (!didFlashInitiated) {
            flashNotice("Ingredient replacement in progress ...");
            didFlashInitiated = true;
          }
        };
        const maybeClear = () => {
          if (didFlashInitiated) flashClear();
        };

        /// The Replace Ingredient endpoint returns the token of a StashedFile,
        /// which we'll use to poll for the status of the background job.
        const initialStashedFileResponse = await response.json();
        const token = initialStashedFileResponse.token;

        const startTime = Date.now();
        while (Date.now() - startTime < timeout) {
          await new Promise((resolve) => setTimeout(resolve, pollingInterval));

          const statusResponse = await fetch(stashedFilePath(token), {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              "Accept": "application/json",
            },
          });

          if (statusResponse.ok) {
            const statusResponseJson = await statusResponse.json();

            const status = statusResponseJson.status;
            if (status === "ready") {
              const message = statusResponseJson.progress_message;
              maybeClear();
              flashSuccess(message || "Success");
              handleComplete();
              break;
            } else if (status === "error") {
              maybeClear();
              flashError(`Error performing ingredient replacement. ${errorHelpTip}`);
              break;
            } else {
              maybeFlashInitiated();
              console.info(`[${token}] status: ${status}`, statusResponseJson);
            }
          }
        }

        if (Date.now() - startTime >= timeout) {
          console.error(
            `Timed out waiting for ingredient replacement job ... token=${token} props=${props} recipeIds=${allRecipeIds}`
          );
          flashError(`Could not confirm successful ingredient replacement. ${errorHelpTip}`);
          ingredientSearchStore.loadIngredientPage(undefined).catch(console.error);
        }
      } else {
        const responseJson = await response.json();
        flashError(`Error: ${responseJson.error_message || "Failed replacing ingredient."}`);
      }
    } catch (error) {
      console.error(`Error: ${error}`);
      flashError(`Error performing ingredient replacement. ${errorHelpTip}`);
    }
  }

  function handleComplete() {
    recipeSearchStore.resetSearch();
  }
</script>

<style scoped>
  @import "~jose_modern/css";

  .modal-card {
    width: 80%;
    max-width: min(85%, 1280px);
    display: flex;
    flex-direction: column;
  }

  .modal-card-head,
  .modal-card-foot,
  .modal-card-subhead {
    flex: 0 0 auto;
  }

  .modal-card-body {
    display: flex;
    flex-direction: column;
    padding: 1.5rem;
  }

  .modal-card-body {
    .radio {
      user-select: none;
    }

    .radio:first-child,
    .radio + .radio {
      margin-right: 0.5rem;
      margin-left: 0;
    }
  }

  .search-container {
    flex: 0 0 auto;
    padding-bottom: 1rem;
  }

  .list-container {
    height: fit-content;

    @include from($tablet) {
      min-height: 575px;
      margin-left: 24px;
      margin-right: 48px;
      margin-top: -6px;
    }

    @include until($tablet) {
      min-height: 50vh;
    }
  }

  .modal-card-subhead {
    padding: 1rem 1.5rem;
  }

  .modal-card-body p {
    margin-bottom: 1.5em;
  }

  .well-beneath-search-field.scope-filter-options {
    margin-bottom: 0.5rem;

    @include until($tablet) {
      margin-bottom: 0.25rem;
    }
  }

  .scope-filter-options {
    .control {
      padding: 0.25rem 0.5rem 0;
    }

    @include until($desktop) {
      .control {
        padding: 0.125rem 0.25rem 0.375rem;
      }

      .column {
        flex: 0 0 70%;
      }

      .label,
      .radio {
        font-size: 0.9rem;
      }
    }

    @include until($tablet) {
      .control {
        display: flex;
        flex-direction: column;
      }

      .column {
        flex: 0 0 50%;
        padding: 0.25rem;
      }

      .label,
      .radio {
        font-size: 0.8rem;
        margin-bottom: 5px;
      }

      .label {
        margin-bottom: 0;
      }
    }
  }

  @include until($desktop) {
    .modal-card-subhead {
      padding-bottom: 0;
    }
  }

  @include until($tablet) {
    .modal-card-subhead {
      font-size: 0.8rem;
    }

    .modal-card-body {
      padding: 5px 10px 10px;
    }

    .modal-card-subhead {
      padding: 15px;
    }

    .modal-card-head {
      padding: 10px;
    }

    .modal-card-title {
      font-size: 1rem;
      font-weight: 600;
    }
  }
</style>
